import {
  deserify,
  IconWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  SelectMenu,
} from '@geneo2-web/shared-ui';
import { Box, Checkbox, MenuItem, Typography } from '@mui/material';
import { Dispatch, ReactNode, SetStateAction } from 'react';
import { useAppSelector } from '../../../../reduxStore/reduxHooks';
import { IQuestionPaperFilter } from '../../home.utils';
import { QuestionPaperType } from '@protos/content_management/content.db_pb';
import { IGetQuestionPapers } from '../PreviousYearQPList';

const styles: IStyles = {
  root: {},

  tabBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(65) },
    flex: 1,
    marginTop: { xs: pxToRem(25), md: pxTovW(55) },
    marginBottom: { xs: pxToRem(20), md: pxTovW(40) },
  },
  selectedTab: {
    backgroundColor: 'primary.main',
    paddingX: { xs: pxToRem(7), md: pxTovW(14) },
    paddingY: { xs: pxToRem(10), md: pxTovW(20) },
    '&:hover': {
      backgroundColor: '#006BB5',
    },
  },
  notSelectedTab: {
    paddingX: { xs: pxToRem(7), md: pxTovW(14) },
    paddingY: { xs: pxToRem(10), md: pxTovW(20) },
    '&:hover': {
      backgroundColor: '#E6F3FB',
    },
  },

  allInputBox: {
    display: { xs: 'block', md: 'flex' },
    gap: { xs: pxToRem(15), md: pxTovW(30) },
    alignItems: 'end',
    justifyContent: 'center',
  },
  inputContainer: {
    display: 'flex',
    gap: { xs: pxToRem(15), md: pxTovW(30) },
    alignItems: 'end',
    justifyContent: 'space-between', // optional, helps spacing
    '& > *': { flex: 1 },
    marginTop: { xs: pxToRem(15), md: pxTovW(0) },
  },

  // CheckBoxMenuItem
  checkboxWithItem: {
    borderBottom: '1px solid #96FFC5',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: '#ECFFF5' },
    paddingX: { xs: pxToRem(10), md: pxTovW(10) },
    paddingY: { xs: pxToRem(15), md: pxTovW(12) },
    display: 'flex',
    gap: { xs: pxToRem(5), md: pxTovW(5) },
    alignItems: 'center',
    borderRadius: { xs: pxToRem(3), md: pxTovW(3) },
    width: { xs: 'inherit', md: pxTovW(215) },
  },
  checkboxIcon: {
    width: { xs: pxToRem(15), md: pxTovW(20) },
    height: { xs: pxToRem(15), md: pxTovW(20) },
    backgroundColor: 'white',
    border: '1px solid #93CEFC',
    borderRadius: { xs: pxToRem(3), md: pxTovW(3) },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
  checkbox: {
    padding: 0,
    boxSizing: 'border-box',
    maxWidth: 'max-content',
  },
  checkBoxMenuItemTypo: {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
};

interface IProps {
  boardsList?: IQuestionPaperFilter[];
  setBoardsList: Dispatch<SetStateAction<IQuestionPaperFilter[]>>;
  classesList?: IQuestionPaperFilter[];
  setClassesList: Dispatch<SetStateAction<IQuestionPaperFilter[]>>;
  subjectsList?: IQuestionPaperFilter[];
  setSubjectsList: Dispatch<SetStateAction<IQuestionPaperFilter[]>>;
  yearsList?: IQuestionPaperFilter[];
  setYearsList: Dispatch<SetStateAction<IQuestionPaperFilter[]>>;
  setPageNumber: Dispatch<SetStateAction<number>>;
  getQuestionPapers: (props?: IGetQuestionPapers) => Promise<void>;
}

export const FiltersContainerQP = (props: IProps) => {
  const {
    boardsList,
    setBoardsList,
    classesList,
    setClassesList,
    subjectsList,
    setSubjectsList,
    yearsList,
    setYearsList,
    setPageNumber,
    getQuestionPapers,
  } = props;

  const { question_paper_data } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );

  return (
    <>
      <Box sx={styles.inputContainer}>
        <CustomSelectMenu
          label="Select Board"
          value={
            boardsList
              ?.filter((item) => item.checked)
              ?.map((item) => item.name)
              .join(', ') ?? ''
          }
          menuNode={
            <Box>
              {boardsList?.map((listData) => (
                <CheckBoxMenuItem
                  key={listData.enum}
                  checked={listData.checked}
                  showCheckBox={boardsList.length > 1}
                  onChangeCheckbox={() => {
                    setBoardsList((prevState) => {
                      const modifiedData = prevState?.map((item) =>
                        item.enum === listData.enum
                          ? { ...item, checked: !item.checked }
                          : item
                      );
                      getQuestionPapers({ currentBoardsList: modifiedData });
                      return modifiedData;
                    });
                    setPageNumber(1);
                  }}
                  menuText={listData.name}
                />
              ))}
            </Box>
          }
          clearIconHandler={() => {
            setBoardsList((prevState) => {
              const modifiedData = prevState?.map((item) => {
                return { ...item, checked: false };
              });
              getQuestionPapers({ currentBoardsList: modifiedData });
              return modifiedData;
            });
            setPageNumber(1);
          }}
        />

        <CustomSelectMenu
          label="Choose Class"
          value={
            classesList
              ?.filter((item) => item.checked)
              ?.map((item) => item.name)
              .join(', ') ?? ''
          }
          menuNode={
            <Box>
              {classesList && classesList.length > 1 ? (
                <MenuItem
                  key={0}
                  onClick={() => {
                    setClassesList((prevState) => {
                      const modifiedData = prevState?.map((item) => {
                        return { ...item, checked: true };
                      });
                      getQuestionPapers({ currentClassesList: modifiedData });
                      return modifiedData;
                    });
                    setPageNumber(1);
                  }}
                  sx={styles.checkboxWithItem}
                >
                  <Typography variant="h5" sx={styles.checkBoxMenuItemTypo}>
                    All
                  </Typography>
                </MenuItem>
              ) : null}

              {classesList?.map((listData) => (
                <CheckBoxMenuItem
                  key={listData.enum}
                  checked={listData.checked}
                  showCheckBox={classesList.length > 1}
                  onChangeCheckbox={() => {
                    setClassesList((prevState) => {
                      const modifiedData = prevState?.map((item) =>
                        item.enum === listData.enum
                          ? { ...item, checked: !item.checked }
                          : item
                      );
                      getQuestionPapers({ currentClassesList: modifiedData });
                      return modifiedData;
                    });
                    setPageNumber(1);
                  }}
                  menuText={listData.name}
                />
              ))}
            </Box>
          }
          clearIconHandler={() => {
            setClassesList((prevState) => {
              const modifiedData = prevState?.map((item) => {
                return { ...item, checked: false };
              });
              getQuestionPapers({ currentClassesList: modifiedData });
              return modifiedData;
            });
            setPageNumber(1);
          }}
        />
      </Box>

      <Box sx={styles.inputContainer}>
        <CustomSelectMenu
          label="Choose Subject"
          fullWidthMobile={
            question_paper_data?.selected_tab === QuestionPaperType.SAMPLE
          }
          value={
            subjectsList
              ?.filter((item) => item.checked)
              ?.map((item) => item.name)
              .join(', ') ?? ''
          }
          menuNode={
            <Box>
              {subjectsList?.map((listData) => (
                <CheckBoxMenuItem
                  key={listData.enum}
                  fullWidthMobile={
                    question_paper_data?.selected_tab ===
                    QuestionPaperType.SAMPLE
                  }
                  checked={listData.checked}
                  showCheckBox={subjectsList.length > 1}
                  onChangeCheckbox={() => {
                    setSubjectsList((prevState) => {
                      const modifiedData = prevState?.map((item) =>
                        item.enum === listData.enum
                          ? { ...item, checked: !item.checked }
                          : item
                      );
                      getQuestionPapers({ currentSubjectsList: modifiedData });
                      return modifiedData;
                    });
                    setPageNumber(1);
                  }}
                  menuText={listData.name}
                />
              ))}
            </Box>
          }
          clearIconHandler={() => {
            setSubjectsList((prevState) => {
              const modifiedData = prevState?.map((item) => {
                return { ...item, checked: false };
              });
              getQuestionPapers({ currentSubjectsList: modifiedData });
              return modifiedData;
            });
            setPageNumber(1);
          }}
        />

        {question_paper_data?.selected_tab ===
        QuestionPaperType.PREVIOUS_YEAR ? (
          <CustomSelectMenu
            label="Select Year"
            value={
              yearsList
                ?.filter((item) => item.checked)
                ?.map((item) => item.name)
                .join(', ') ?? ''
            }
            menuNode={
              <Box>
                {yearsList?.map((listData) => (
                  <CheckBoxMenuItem
                    key={listData.enum}
                    checked={listData.checked}
                    showCheckBox={yearsList.length > 1}
                    onChangeCheckbox={() => {
                      setYearsList((prevState) => {
                        const modifiedData = prevState?.map((item) =>
                          item.enum === listData.enum
                            ? { ...item, checked: !item.checked }
                            : item
                        );
                        getQuestionPapers({ currentYearList: modifiedData });
                        return modifiedData;
                      });
                      setPageNumber(1);
                    }}
                    menuText={listData.name}
                  />
                ))}
              </Box>
            }
            clearIconHandler={() => {
              setYearsList((prevState) => {
                const modifiedData = prevState?.map((item) => {
                  return { ...item, checked: false };
                });
                getQuestionPapers({ currentYearList: modifiedData });
                return modifiedData;
              });
              setPageNumber(1);
            }}
          />
        ) : null}
      </Box>
    </>
  );
};

// ------------------------------------------------------------

interface ICustomSelectMenu {
  label: string;
  value: string;
  menuNode?: ReactNode;
  fullWidthMobile?: boolean;
  clearIconHandler?: () => void;
}
const CustomSelectMenu = (props: ICustomSelectMenu) => {
  const { label, value, menuNode, fullWidthMobile, clearIconHandler } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: pxToRem(5), md: pxTovW(10) },
      }}
    >
      <Typography variant="h3">{label}</Typography>
      <SelectMenu
        placeholder="Select"
        value={value}
        onChange={(data) => {
          if (data === '' && clearIconHandler) {
            // only used when clearing
            clearIconHandler();
          }
        }}
        optionList={[]}
        menuNode={menuNode}
        clearIcon
        rootStyles={{
          maxWidth: {
            xs: fullWidthMobile ? '90vw' : '42vw',
            md: 'unset',
            lg: 'unset',
          },
          height: { xs: pxToRem(55), md: pxTovW(75), lg: pxTovW(65) },
          boxSizing: 'border-box',
        }}
        selectedBold
        menuItemStyles={{
          paddingX: { xs: pxToRem(10), md: pxTovW(20) },
          paddingY: { xs: pxToRem(6), md: pxTovW(13) },
        }}
        menuPaperProps={{
          minWidth: { xs: '95%', md: 'max-content' },
          maxWidth: { xs: '95%', md: 'unset' },
        }}
      />
    </Box>
  );
};

// ------------------------------------------------------------

interface ICheckBoxMenuItem {
  checked: boolean;
  onChangeCheckbox: () => void;
  menuText: string;
  fullWidthMobile?: boolean;
  showCheckBox?: boolean;
}
const CheckBoxMenuItem = (props: ICheckBoxMenuItem) => {
  const { checked, onChangeCheckbox, menuText, fullWidthMobile, showCheckBox } =
    props;
  return (
    <MenuItem
      onClick={(e) => {
        showCheckBox && e.stopPropagation();
        onChangeCheckbox();
      }}
      sx={{
        ...styles.checkboxWithItem,
        width: { xs: fullWidthMobile ? '90vw' : '42vw', md: pxTovW(215) },
      }}
    >
      {showCheckBox ? (
        <Checkbox
          checked={checked}
          // onChange={onChangeCheckbox}
          icon={<Box sx={styles.checkboxIcon}></Box>}
          checkedIcon={
            <Box sx={{ ...styles.checkboxIcon, border: '1px solid #007CDC' }}>
              <IconWrapper
                name="check-black"
                type="png"
                parentFolder="icons"
                size="small"
              />
            </Box>
          }
          sx={styles.checkbox}
        />
      ) : null}

      <Typography
        variant="h5"
        sx={styles.checkBoxMenuItemTypo}
        fontWeight={checked ? 'bold' : 'regular'}
      >
        {menuText}
      </Typography>
    </MenuItem>
  );
};

// ------------------------------------------------------------
